import { useState, useRef, useMemo } from 'react';
import { useInventory } from '../../contexts/InventoryContext';
import { BULK_ACTIONS, getVisibleActions } from '../../contexts/inventoryBulkActions';
import MoreActionsMenu from './bulk_actions/MoreActionsMenu';

/**
 * BulkActionBar — selection bar driven by the inventoryBulkActions
 * registry. Slides up from the bottom when selection > 0.
 *
 * Selection persists across filter/view/tab changes. The bar shows
 * "N selected · M hidden by filter" with a [Show all] affordance to
 * clear filters and reveal the hidden selection.
 *
 * Props:
 *   caseId, documents, folderPaths, onDocChange, onChatScope — as before
 *   visibleSelectedCount — # selected docs in the current filter result
 *                          (passed by parent so we don't recompute)
 */
export default function BulkActionBar({
  caseId,
  documents,
  folderPaths = [],
  onDocChange,
  onChatScope,
  visibleSelectedCount = 0,
  extraContext = null,
}) {
  const { dispatch, ...state } = useInventory();
  const count = state.selected.size;
  const ids = useMemo(() => Array.from(state.selected), [state.selected]);

  // Selected doc objects, for isApplicable gating.
  const selectedDocs = useMemo(() => {
    if (count === 0) return [];
    return documents.filter((d) => state.selected.has(d.id));
  }, [documents, state.selected, count]);

  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const statusTimerRef = useRef(null);
  const [openAction, setOpenAction] = useState(null);  // action.key currently showing popover
  const anchorRefs = useRef({});                       // per-action button refs
  const moreWrapRef = useRef(null);                    // anchor for 'more'-surface popovers

  function setStatusTimed(s) {
    setStatus(s);
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    if (s) {
      statusTimerRef.current = setTimeout(() => setStatus(null), 8000);
    }
  }

  function getAnchorRef(key) {
    if (!anchorRefs.current[key]) {
      anchorRefs.current[key] = { current: null };
    }
    return anchorRefs.current[key];
  }

  // Slide-up/down via CSS class. Bar is always in the DOM (so popovers
  // can finish their animation cleanly) but visually hidden when there's
  // no selection AND no result status to show.
  const open = count > 0 || Boolean(status);

  const groupedActions = getVisibleActions(selectedDocs, { onChatScope, ...(extraContext || {}) });

  async function runDirect(action) {
    setBusy(true);
    setStatus(null);
    try {
      const ctx = { caseId, ids, count, documents, onDocChange, onChatScope, ...(extraContext || {}) };
      const result = await action.handler(ctx);
      setStatusTimed({ text: result?.label || `${action.label} done`, error: Boolean(result?.error) });
      dispatch({ type: 'CLEAR_SELECTION' });
    } catch (err) {
      setStatusTimed({ text: `${action.label} failed: ${err.message}`, error: true });
    } finally {
      setBusy(false);
    }
  }

  function handlePick(action) {
    if (busy) return;
    if (action.kind === 'popover') {
      setOpenAction((cur) => (cur === action.key ? null : action.key));
    } else {
      runDirect(action);
    }
  }

  function handlePopoverCompleted(label) {
    setStatusTimed({ text: label, error: false });
    setOpenAction(null);
    dispatch({ type: 'CLEAR_SELECTION' });
    onDocChange?.();
  }

  // Render a popover for an action, anchored to the given ref. Shared by
  // inline buttons and the 'more' menu so every popover action actually
  // mounts its Popover when opened.
  function renderPopover(action, anchorRefObj, placement) {
    if (!action || action.kind !== 'popover' || !action.Popover) return null;
    const Popover = action.Popover;
    return (
      <Popover
        caseId={caseId}
        ids={ids}
        count={count}
        documents={documents}
        folderPaths={folderPaths}
        anchorRef={anchorRefObj}
        placement={placement}
        onClose={() => setOpenAction(null)}
        onCompleted={handlePopoverCompleted}
      />
    );
  }

  function renderActionButton(action) {
    const isOpen = openAction === action.key;
    const cls = [
      'bulk-action-btn',
      action.variant === 'destructive' ? 'bulk-action-btn--destructive' : '',
      action.variant === 'primary' ? 'bulk-action-btn--primary' : '',
      action.variant === 'secondary' ? 'bulk-action-btn--secondary' : '',
      isOpen ? 'bulk-action-btn--open' : '',
    ].filter(Boolean).join(' ');

    return (
      <div key={action.key} className="bulk-action-wrap">
        <button
          ref={(el) => { getAnchorRef(action.key).current = el; }}
          type="button"
          className={cls}
          onClick={() => handlePick(action)}
          disabled={busy}
        >
          <span className="bulk-action-icon">{action.icon}</span>
          <span className="bulk-action-label">{action.label}</span>
        </button>
        {isOpen && renderPopover(
          action,
          getAnchorRef(action.key),
          action.surface === 'inline-right' ? 'right' : 'left',
        )}
      </div>
    );
  }

  // The currently-open 'more'-surface popover action, if any. These live
  // in the More ▾ menu, so their popover is rendered at the bar level
  // (anchored to the More menu) rather than inside renderActionButton.
  const openMoreAction = groupedActions.more.find(
    (a) => a.key === openAction && a.kind === 'popover',
  );

  return (
    <div className={`inv-bulk-bar ${open ? 'inv-bulk-bar--open' : ''}`} aria-hidden={!open}>
      {count > 0 && (
        <span className="inv-bulk-count">
          {count} selected
          {visibleSelectedCount < count && (
            <>
              <span className="inv-bulk-hidden">
                {' · '}{count - visibleSelectedCount} hidden by filter
              </span>
              <button
                type="button"
                className="inv-bulk-show-all"
                onClick={() => dispatch({ type: 'RESET_FILTERS' })}
                title="Clear filters to reveal hidden selections"
              >
                Show all
              </button>
            </>
          )}
        </span>
      )}

      {count > 0 && (
        <div className="inv-bulk-actions">
          {groupedActions.inline.map(renderActionButton)}
          <div className="bulk-action-wrap" ref={moreWrapRef}>
            <MoreActionsMenu actions={groupedActions.more} busy={busy} onPick={handlePick} />
            {openMoreAction && renderPopover(openMoreAction, moreWrapRef, 'left')}
          </div>
          <div className="bulk-action-spacer" />
          {groupedActions.inlineRight.map(renderActionButton)}
          <button
            type="button"
            className="bulk-action-btn bulk-action-btn--ghost"
            onClick={() => dispatch({ type: 'CLEAR_SELECTION' })}
            title="Clear selection"
            disabled={busy}
          >
            ✕
          </button>
        </div>
      )}

      {status && (
        <div className={`inv-bulk-status ${status.error ? 'error' : ''}`}>
          {status.text}
        </div>
      )}
    </div>
  );
}
